import { Button } from '#/components/ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '#/components/ui/hover-card';
import { CopyToClipboard } from '#/components/v2/copy-to-clipboard';
import { useTranslation } from '#/contexts/useTranslation';
import {
  useConfiguredBlockExplorer,
  useConfiguredChainId,
} from '#/data/wagmi/configured';
import { cn } from '#/lib/utils';
import { Translation } from 'intl-lib';
import { Copy, ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { ReactNode, useMemo } from 'react';
import { Address } from 'viem';
import {
  consolidationConfig,
  directStakingConfig,
  executionWithdrawalConfig,
  rewardPoolConfig,
} from '../lib/generated';
import EthereumAccountAvatar from './EthereumAccountAvatar';
import EthereumAccountBalanceOf from './EthereumAccountBalanceOf';
import EthereumHexString from './EthereumHexString';
import { Badge } from './ui/badge';

interface Props {
  address?: Address | null;
  diameter?: number;
  className?: string | null;
  subtitle?: ReactNode | null;
}

export default function EthereumAccount({
  address,
  diameter,
  className,
  subtitle,
}: Props) {
  const translation = useTranslation(
    translation => translation.EthereumStaking
  );
  const blockExplorer = useConfiguredBlockExplorer();
  const chainId = useConfiguredChainId();
  const isOfficial = useMemo(
    () =>
      address === directStakingConfig.address(chainId) ||
      address === rewardPoolConfig.address(chainId) ||
      address === consolidationConfig.address(chainId) ||
      address === executionWithdrawalConfig.address(chainId),
    [address, chainId]
  );
  return (
    <HoverCard>
      <HoverCardTrigger asChild tabIndex={-1}>
        <button
          type="button"
          className={cn('flex items-center gap-2', className)}
        >
          <EthereumAccountAvatar
            address={address}
            diameter={20}
            className={cn('w-5 h-5', {
              'w-8 h-8': subtitle != null,
            })}
          />
          <div className="flex flex-col items-start">
            <span
              className={cn(
                'font-mono underline underline-offset-4 decoration-dashed',
                isOfficial ? 'decoration-blue-600' : 'decoration-gray-300'
              )}
            >
              <EthereumHexString value={address} diameter={diameter} />
            </span>
            {subtitle}
          </div>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        autoFocus={false}
        className="w-full max-w-(--breakpoint-sm)"
      >
        <div className="flex flex-col gap-2">
          {isOfficial && (
            <div>
              <Badge variant="outline">RockX Official</Badge>
            </div>
          )}
          <div className="flex justify-between items-center gap-2 py-2">
            <div className="text-gray-500">
              {address ? (
                <div className="flex items-center gap-2">
                  <EthereumAccountAvatar
                    address={address}
                    diameter={32}
                    className="w-8 h-8"
                  />
                  <div className="flex flex-col">
                    <div className="font-mono text-base text-gray-900">
                      <EthereumHexString value={address} diameter={16} />
                    </div>
                    <div className="text-sm">
                      <EthereumAccountBalanceOf address={address} />
                    </div>
                  </div>
                </div>
              ) : (
                '-'
              )}
            </div>
          </div>
          <div className="flex gap-4">
            <CopyToClipboard text={address}>
              <Button variant="secondary" size="sm">
                <Copy className="w-4 h-4 mr-2" />
                {translation.AccountHoverCard.CopyAddressButtonLabel}
              </Button>
            </CopyToClipboard>
            <Button variant="secondary" size="sm" asChild>
              <Link
                href={`${blockExplorer?.url}/address/${address}`}
                target="_blank"
                rel="noreferrer"
                prefetch={false}
              >
                <ExternalLink className="w-4 h-4" />
                <Translation
                  path="global.noun.view_on_button_label"
                  params={blockExplorer}
                />
              </Link>
            </Button>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
